import React, { memo, useContext } from 'react'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import { parseRunSlugFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunSlugFromPathname'
import useIsWatchingGeneratingRun from 'Pods/ThreeDStudio/Hooks/useIsWatchingGeneratingRun'
import LoadingState from 'Pods/ThreeDStudio/Shared/LoadingState/LoadingState'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import InitialBannerState from './Components/InitialBannerState/InitialBannerState'
import LoadingBannerState from './Components/LoadingBannerState/LoadingBannerState'
import UploadingBannerState from './Components/UploadingBannerState/UploadingBannerState'

function Banner() {
  // Only the run named by the URL replaces the starting screen with its progress state:
  // a run in flight elsewhere leaves bare /ai alone. See useIsWatchingGeneratingRun.
  const isWatchingGeneratingRun = useIsWatchingGeneratingRun()
  const isModelUploading = useThreeDStudioStore(state => state.ui?.isModelUploading)
  const location = useLocation()
  // Keyed on the slug, not on a trailing numeric id: a persisted Generator::Run#slug
  // ends in a random token (battle-mech-FlaCqrS0afs), so an id-shaped check would miss
  // every shared link and flash the create/upload state while useModelUrlSync fetches.
  const isLoadingModelFromUrl = parseRunSlugFromPathname(location?.pathname ?? '') != null
  const { extractingCoreStyles } = useContext(ThreeDStudioPageContext)

  if (extractingCoreStyles) {
    return (
      <div className="extraction-versions-wrapper">
        <InitialBannerState />
        <LoadingBannerState />
      </div>
    )
  }

  // Uploading wins over a model-from-URL load: it reflects the user's most
  // recent action, and the two can briefly overlap before navigate('/ai').
  if (isModelUploading) {
    return <UploadingBannerState />
  }

  if (isWatchingGeneratingRun) {
    return <LoadingBannerState />
  }

  // Opening a shared model hands straight over to ViewerContent's own overlay once the
  // payload lands, so the fetch reuses that overlay: one uninterrupted spinner rather
  // than a card swapped for a different spinner a moment later.
  if (isLoadingModelFromUrl) {
    return <LoadingState />
  }

  return <InitialBannerState />
}

export default memo(Banner)
